/**
 * resolveKeviaSuggestions.js
 *
 * Generates contextual conversation-starter suggestions for Kevia's welcome screen.
 * Priority order: entity context → active signals → module → role → default.
 *
 * Design rules:
 *  - Deterministic, no LLM
 *  - Suggestions sound pastoral, not corporate
 *  - Entity context always takes priority
 *  - 3-4 suggestions maximum
 */

import { getActiveSignals, SIGNALS } from "../kevia-signals";

/* ─── Module suggestion registry ─────────────────────────────────────── */

const MODULE_SUGGESTIONS = {
  operaciones: [
    "Muéstrame actividad reciente del distrito",
    "¿Hay alertas pastorales activas?",
    "Abrir transferencias pendientes",
    "Verifica la integridad de los datos",
  ],
  "auditoría": [
    "Últimos eventos registrados en el sistema",
    "¿Qué cambios administrativos ocurrieron recientemente?",
    "Actividad de coordinadores esta semana",
  ],
  iglesias: [
    "¿Qué iglesias necesitan atención pastoral?",
    "Resumen de visitas por congregación",
    "¿Hay materiales pendientes de alguna iglesia?",
    "Iglesias sin actividad reciente",
  ],
  familias: [
    "¿Qué familias necesitan seguimiento prioritario?",
    "Familias sin visitas recientes",
    "Mostrar señales pastorales relevantes",
    "¿Cómo fortalecer el seguimiento familiar?",
  ],
  personas: [
    "¿Quién necesita atención pastoral urgente?",
    "Miembros sin seguimiento reciente",
    "¿Cómo identificar líderes emergentes?",
  ],
  visitas: [
    "Visitas pendientes para esta semana",
    "Seguimiento de visitas recientes",
    "¿Qué familias no se han visitado?",
    "Cómo preparar una visita pastoral efectiva",
  ],
  oraciones: [
    "Solicitudes de oración activas",
    "¿Cómo interceder mejor por estas necesidades?",
    "Reflexión sobre el ministerio de oración",
  ],
  predicaciones: [
    "Ayúdame a preparar un sermón",
    "Ideas para el próximo estudio bíblico",
    "¿Qué temas son relevantes para la congregación?",
  ],
  materiales: [
    "Estado de materiales pendientes",
    "Resumen financiero del mes",
    "¿Hay distribuciones sin confirmar?",
  ],
  usuarios: [
    "Solicitudes de acceso pendientes",
    "Coordinar permisos del sistema",
  ],
  "junta directiva": [
    "¿Quiénes son los miembros actuales de la directiva?",
    "Funciones y responsabilidades del equipo",
    "Cómo fortalecer el equipo de liderazgo",
  ],
  notas: [
    "Notas pastorales más recientes",
    "¿Qué seguimientos están pendientes?",
    "Reflexiones sobre el ministerio",
  ],
  eventos: [
    "Próximos eventos del distrito",
    "Eventos sin resultados registrados",
    "Cómo planificar un evento pastoral efectivo",
  ],
  "revisión": [
    "Checklist de revisión mensual",
    "¿Qué iglesias tienen revisiones pendientes?",
    "Estado general de la revisión del distrito",
  ],
};

/* ─── Entity-context suggestions ─────────────────────────────────────── */

function getEntitySuggestions(entityContext) {
  const name = entityContext.name ?? "esta entidad";
  switch (entityContext.type) {
    case "person":
      return [
        `¿Cómo está el seguimiento pastoral de ${name}?`,
        "¿Hay señales pastorales que deba atender?",
        "Ayúdame a planear una visita",
      ];
    case "family":
      return [
        `¿Cómo está la familia ${name}?`,
        "¿Cuándo fue la última visita?",
        "Resumen pastoral de esta familia",
      ];
    case "church":
      return [
        `Resumen pastoral de ${name}`,
        "¿Qué familias necesitan atención?",
        "Indicadores pastorales de esta congregación",
      ];
    case "visit":
      return [
        "¿Cuáles son los próximos pasos de esta visita?",
        "Reflexión pastoral sobre este seguimiento",
      ];
    case "prayer":
      return [
        "¿Cómo abordar esta solicitud de oración?",
        "¿Hay seguimiento pastoral pendiente?",
      ];
    default:
      return null;
  }
}

/* ─── Signal-based suggestions ───────────────────────────────────────── */

const SIGNAL_SUGGESTIONS = {
  [SIGNALS.FOLLOW_UP_RISK]: [
    "¿Qué próximos pasos recomiendas para estas familias?",
    "Ayúdame a priorizar los seguimientos pendientes",
    "¿Cómo preparar una visita pastoral efectiva?",
  ],
  [SIGNALS.INACTIVE_FAMILY_PATTERN]: [
    "¿Qué familias llevan más tiempo sin visita?",
    "Ayúdame a planificar visitas para esta semana",
    "¿Cómo abordar el seguimiento de familias distantes?",
  ],
  [SIGNALS.HIGH_VISIT_ACTIVITY]: [
    "¿Cómo consolidar el seguimiento de visitas recientes?",
    "Reflexión pastoral sobre las visitas de esta semana",
    "Próximos pasos después de una visita pastoral",
  ],
  [SIGNALS.AUDIT_INVESTIGATION]: [
    "¿Qué eventos administrativos debo revisar?",
    "Ayúdame a interpretar los registros del sistema",
    "¿Hay actividad inusual que deba atender?",
  ],
  [SIGNALS.PRAYER_FOCUS]: [
    "¿Cómo interceder mejor por estas peticiones?",
    "Reflexión sobre el ministerio de oración",
    "¿Hay peticiones urgentes que requieran seguimiento?",
  ],
  [SIGNALS.MEMBER_RECOVERY_FLOW]: [
    "¿Cómo acompañar pastoralmente en este proceso?",
    "Reflexión sobre el seguimiento de este miembro",
    "¿Cuáles son los próximos pasos en este acompañamiento?",
  ],
  [SIGNALS.LEADERSHIP_REVIEW]: [
    "¿Cómo fortalecer el equipo de liderazgo?",
    "¿Cuáles son las responsabilidades actuales de la directiva?",
    "Reflexión sobre el liderazgo pastoral del distrito",
  ],
};

/* ─── Default fallbacks ───────────────────────────────────────────────── */

const DEFAULT_SUGGESTIONS = [
  "¿Cómo identificar familias que necesitan seguimiento?",
  "Ayúdame a planificar visitas pastorales",
  "Reflexión sobre el ministerio y el servicio",
  "Dame ideas para un sermón",
];

/* ─── Public API ──────────────────────────────────────────────────────── */

/**
 * @param {{ module?: string, role?: string, entityContext?: object, tier?: string }} opts
 * @returns {{ text: string }[]}  — 3-4 suggestions
 */
export function resolveKeviaSuggestions({ module, role, entityContext, tier } = {}) {
  // 1. Entity context always wins
  if (entityContext?.type) {
    const sug = getEntitySuggestions(entityContext);
    if (sug?.length) return sug.map((text) => ({ text }));
  }

  // 2. Active operational signals — session-aware layer
  try {
    const signals = getActiveSignals();
    if (signals.length > 0) {
      const topSignal = signals[0];
      const signalSugs = SIGNAL_SUGGESTIONS[topSignal.id];
      if (signalSugs?.length) {
        return signalSugs.slice(0, 3).map((text) => ({ text }));
      }
    }
  } catch {
    // signals not available (e.g. cold start) — fall through
  }

  // 3. Module-specific
  if (module) {
    const moduleSugs = MODULE_SUGGESTIONS[module];
    if (moduleSugs?.length) {
      const limit = tier === "plus" ? 4 : 3;
      return moduleSugs.slice(0, limit).map((text) => ({ text }));
    }
  }

  // 4. Role fallbacks
  if (role === "coordinator") {
    return [
      "¿Qué familias de mi iglesia necesitan seguimiento?",
      "Ayúdame a organizar las visitas de esta semana",
      "Reflexión sobre el acompañamiento congregacional",
    ].map((text) => ({ text }));
  }

  // 5. Default
  return DEFAULT_SUGGESTIONS.slice(0, 3).map((text) => ({ text }));
}
